import type { AreaFeature, LinearRing, PolygonCoordinates } from "./apiTypes";

export type LatLngPoint = [number, number];

function pointOnSegment(lng: number, lat: number, a: [number, number], b: [number, number]): boolean {
  const cross = (lat - a[1]) * (b[0] - a[0]) - (lng - a[0]) * (b[1] - a[1]);
  if (Math.abs(cross) > 1e-10) {
    return false;
  }
  const dot = (lng - a[0]) * (b[0] - a[0]) + (lat - a[1]) * (b[1] - a[1]);
  if (dot < 0) {
    return false;
  }
  const squaredLength = (b[0] - a[0]) ** 2 + (b[1] - a[1]) ** 2;
  return dot <= squaredLength;
}

function pointInRing(lng: number, lat: number, ring: LinearRing): boolean {
  let inside = false;
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i, i += 1) {
    const current = ring[i];
    const previous = ring[j];
    if (!current || !previous) {
      continue;
    }
    if (pointOnSegment(lng, lat, previous, current)) {
      return true;
    }

    const intersects =
      current[1] > lat !== previous[1] > lat &&
      lng < ((previous[0] - current[0]) * (lat - current[1])) / (previous[1] - current[1]) + current[0];
    if (intersects) {
      inside = !inside;
    }
  }
  return inside;
}

function pointInPolygonCoordinates(lng: number, lat: number, polygon: PolygonCoordinates): boolean {
  const [outerRing, ...holes] = polygon;
  if (!outerRing || !pointInRing(lng, lat, outerRing)) {
    return false;
  }
  return !holes.some((hole) => pointInRing(lng, lat, hole));
}

function insideFeatureBounds(point: LatLngPoint, feature: AreaFeature): boolean {
  const bbox = feature.properties.bbox;
  if (!bbox) {
    return true;
  }
  const [lat, lng] = point;
  const [west, south, east, north] = bbox;
  return lng >= west && lng <= east && lat >= south && lat <= north;
}

function featureAreaScore(feature: AreaFeature): number {
  const bbox = feature.properties.bbox;
  if (bbox) {
    const [west, south, east, north] = bbox;
    return Math.abs((east - west) * (north - south));
  }
  return Number.POSITIVE_INFINITY;
}

export function findContainingArea(point: LatLngPoint, features: AreaFeature[]): AreaFeature | null {
  const [lat, lng] = point;
  let bestFeature: AreaFeature | null = null;
  let bestAreaScore = Number.POSITIVE_INFINITY;

  for (const feature of features) {
    if (!insideFeatureBounds(point, feature)) {
      continue;
    }
    const contains =
      (feature.geometry.type === "Polygon" && pointInPolygonCoordinates(lng, lat, feature.geometry.coordinates)) ||
      feature.geometry.type === "MultiPolygon" &&
        feature.geometry.coordinates.some((polygon) => pointInPolygonCoordinates(lng, lat, polygon));

    if (contains) {
      const areaScore = featureAreaScore(feature);
      if (areaScore < bestAreaScore) {
        bestFeature = feature;
        bestAreaScore = areaScore;
      }
    }
  }
  return bestFeature;
}
